import { useCallback, useContext, useEffect, useState } from "react"
import DefaultCheckbox from "./DefaultCheckbox"
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { debounceFunction } from "../../../../utils/utilFunctions";
import { eachDeep } from "deepdash-es/standalone";
import { cloneDeep, debounce, get, isArray, isEqual, set, template } from "lodash";
import { treeActions } from "../../../../redux/slices/treeSlice";
import { Skeleton } from "@mui/material";
import { LoadingEase } from "../../../LoadingEase";

export const CheckboxesContainer = ({flagAsLoaded, updatedSelectedNode, setUpdatedSelectedNode, sourceNodeInfo, nodeField, modifyUpdatedNode, ftClone, isElement}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //redux
  const parentScreen = useContext(ParentScreenCtx);
  // const queryParams = useSelector((state) => state.queryParams);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
  const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
  const templateHeader = rightTree.tree.templateHeader;

  const [checkboxesLoaded, setCheckboxesLoaded] = useState(false);

  //helper function for getting checkbox string as bool for initial state vals
  const getVal = (propName) => {
    // if (!nodeField) return false;
    let sn = nodeField ? nodeField : updatedSelectedNode;
    if (sn?.[propName] === "TRUE") return true;
    else if (sn?.[propName] === "FALSE") return false;
    else return false;
  }
  
  //checkbox states
  // - REQUIRED
  const [required, setRequired] = useState(getVal("REQUIRED"));
  const [requiredHidden, setRequiredHidden] = useState(true);
  
  // - IS_CDATA
  const [cData, setcData] = useState(getVal("IS_CDATA"));
  const [cDataHidden, setcDataHidden] = useState(true);

  // - ESCAPE_XML_DATA or DONT_ESCAPE_XML if xml element...
  const [escapeXMLData, setEscapeXMLData] = useState(getVal("ESCAPE_XML_DATA"));
  const [escapeXMLDataHidden, setEscapeXMLDataHidden] = useState(true);

  const [escXMLDataInversed, setEscXMLDataInversed] = useState(!getVal("DONT_ESCAPE_XML"));
  const [escXMLDataInversedHidden, setEscXMLDataInversedHidden] = useState(true);

  // - TRIMDATA
  const [trimData, setTrimData] = useState(getVal("TRIMDATA")); 
  const [trimDataHidden, setTrimDataHidden] = useState(true);
  
  // - QUOTE_DATA
  const [quoteData, setQuoteData] = useState(getVal("QUOTE_DATA"));
  const [quoteDataHidden, setQuoteDataHidden] = useState(true);
  
  // - ESCAPE_DATA
  const [escapeData, setEscapeData] = useState(getVal("ESCAPE_DATA"));
  const [escapeDataHidden, setEscapeDataHidden] = useState(true);
  
  // - CONVERT_NULL
  const [convertNull, setConvertNull] = useState(getVal("CONVERT_NULL"));
  const [convertNullHidden, setConvertNullHidden] = useState(true);

  // - INCLUDE_NULL
  const [includeNull, setIncludeNull] = useState(getVal("INCLUDE_NULL"));   
  const [includeNullHidden, setIncludeNullHidden] = useState(true);
  
  // - CONVERT_EMPTY
  const [convertEmpty, setConvertEmpty] = useState(getVal("CONVERT_EMPTY"));
  const [convertEmptyHidden, setConvertEmptyHidden] = useState(true);
  
  // - URLENCODE
  const [URLEncodeData, setURLEncodeData] = useState(getVal("URLENCODE"));
  const [URLEncodeDataHidden, setURLEncodeDataHidden] = useState(true);
  
  // - REPEATING
  const [repeatableField, setRepeatableField] = useState(getVal("REPEATING"));
  const [repeatableFieldHidden, setRepeatableFieldHidden] = useState(true);
  
  // - ROUND
  const [allowFloatRounding, setAllowFloatRounding] = useState(updatedSelectedNode?.ROUND === "TRUE");
  const [allowFloatRoundingHidden, setAllowFloatRoundingHidden] = useState(true);

  useEffect(() => {
    setAllowFloatRounding(updatedSelectedNode?.ROUND === "TRUE");
  }, [updatedSelectedNode]);

  // - SUPPRESS_EMPTY_REP_SEPS
  const [suppressEmptyRepSep, setSuppressEmptyRepSep] = useState(getVal("SUPPRESS_EMPTY_REP_SEPS"));
  const [suppressEmptyRepSepHidden, setSuppressEmptyRepSepHidden] = useState(true);
  const [suppressEmptyRepSepDisabled, setSuppressEmptyRepSepDisabled] = useState(false);

  // - SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER
  const [suppressAttr, setSuppressAttr] = useState(getVal("SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER"));
  const [suppressAttrHidden, setSuppressAttrHidden] = useState(true);

  // - SUPPRESS_EMPTY_RECORDS
  const [suppressEmptyRecords, setSuppressEmptyRecords] = useState(getVal("SUPPRESS_EMPTY_RECORDS"));
  const [suppressEmptyRecordsHidden, setSuppressEmptyRecordsHidden] = useState(true);
  
  // - IGNORE_GLOBAL_SUB
  const [ignoreGlobalSubstitutions, setIgnoreGlobalSubstitutions] = useState(getVal("IGNORE_GLOBAL_SUB"));
  const [ignoreGlobalSubstitutionsHidden, setIgnoreGlobalSubstitutionsHidden] = useState(true);
  
  // - SUPPRESS_DELIMITER
  const [suppressTrailingFieldDelimiter, setSuppressTrailingFieldDelimiter] = useState(getVal("SUPPRESS_DELIMITER"));
  const [suppressTrailingFieldDelimiterHidden, setSuppressTrailingFieldDelimiterHidden] = useState(true);

  const checkboxAll = {
    REQUIRED: required,
    TRIMDATA: trimData,
    QUOTE_DATA: quoteData,
    ESCAPE_DATA: escapeData,
    URLENCODE: URLEncodeData,
    CONVERT_NULL: convertNull,
    INCLUDE_NULL: includeNull,
    ROUND: allowFloatRounding,
    REPEATING: repeatableField,
    CONVERT_EMPTY: convertEmpty,
    SUPPRESS_EMPTY_REP_SEPS: suppressEmptyRepSep,
    IGNORE_GLOBAL_SUB: ignoreGlobalSubstitutions,
    SUPPRESS_DELIMITER: suppressTrailingFieldDelimiter,
  }

  const getParentNode = (selectedNode, translatedData) => {
    let node;
    const parentKey = selectedNode.key.split("-").slice(0, -1).join("-");
    
    eachDeep(cloneDeep(translatedData), (obj) => {
      if (obj && obj.key && obj.key === parentKey) {
        node = obj
        return false;
      }
      if (isArray(obj?.children)) {
        return obj.children;
      }
    })
    return node;
  }

  // const modifyUpdatedNode = (prop, val) => {
  //   if (val) {
  //     setUpdatedSelectedNode(prev => {
  //       return {...prev, [prop]: "TRUE"}
  //     });
  //   } else {
  //     setUpdatedSelectedNode(prev => {
  //       return {...prev, [prop]: null}
  //     });
  //   }
  // }

  //based on template FILETYPE, node FIELDTYPE and other variables, set hidden state or disabled state.
  const setCheckboxVisibility = () => {
    //CDATA Element - only XML Element with no children
    if (
      templateHeader?.FILETYPE === "XML" && 
      updatedSelectedNode?.ICON_DISPLAY === "ELEMENT" && 
      updatedSelectedNode?.children && updatedSelectedNode.children.length === 0
    ) {
      setcDataHidden(false);
    } else {
      setcDataHidden(true);
    }

    //trim data - common - Not FIELDTYPE CONSTANT
    if (
      ["CSV", "DATABASE", "EDI", "DELIMITED", "DELIM_FLD_ID", "HL7", "MULIT_DELIMITED", "SERVLET", "JSON", "XML"].includes(templateHeader.FILETYPE) &&
      !["CONSTANT"].includes( nodeField?.FIELDTYPE ? nodeField.FIELDTYPE : updatedSelectedNode.FIELDTYPE)
    ) {
      setTrimDataHidden(false);
    } else {
      setTrimDataHidden(true);
    }

    //escape xml data - XML only
    if (templateHeader.FILETYPE === "XML") {
      setEscapeXMLDataHidden(false);
      setEscXMLDataInversedHidden(false);
    } else {
      setEscapeXMLDataHidden(true);
      setEscXMLDataInversedHidden(true);
    }
  
    //quote data - common
    if (["CSV", "DATABASE", "EDI", "FIXED_LENGTH", "DELIMITED", "DELIM_FLD_ID", "HL7", "MULIT_DELIMITED", "SERVLET", "JSON", "XML"].includes(templateHeader.FILETYPE)) {
      // Hide Quote Data for XML Attributes 
      if (templateHeader.FILETYPE === "XML" && updatedSelectedNode.ICON_DISPLAY === "ATTRIBUTE") {
        setQuoteDataHidden(true);
        setQuoteData(false);
        modifyUpdatedNode("QUOTE_DATA", null);
      } else {
        setQuoteDataHidden(false);
      }
    } else {
      setQuoteDataHidden(true);
    }

    //URLEncode Data - common
    if (["CSV", "DATABASE", "EDI", "FIXED_LENGTH", "DELIMITED", "DELIM_FLD_ID", "HL7", "MULIT_DELIMITED", "SERVLET", "JSON", "XML"].includes(templateHeader.FILETYPE)) {
      setURLEncodeDataHidden(false);
    } else {
      setURLEncodeDataHidden(true);
    }

    //Ignore Global Substitutions - common
    if (["CSV", "DATABASE", "EDI", "FIXED_LENGTH", "DELIMITED", "DELIM_FLD_ID", "HL7", "MULIT_DELIMITED", "SERVLET", "JSON", "XML"].includes(templateHeader.FILETYPE)) {
      setIgnoreGlobalSubstitutionsHidden(false);
    } else {
      setIgnoreGlobalSubstitutionsHidden(true);
    }

    //Suppress Attributes when Data Empty - common
    if (templateHeader.FILETYPE === "XML" && updatedSelectedNode.ICON_DISPLAY === "ATTRIBUTE") {
      setSuppressAttrHidden(false);
    } else {
      setSuppressAttrHidden(true);
    }

    //Suppress Empty Elements - XML Element
    if (templateHeader.FILETYPE === "XML" && ["ELEMENT", "REPEATING_ELEMENT"].includes(updatedSelectedNode.ICON_DISPLAY)) {
      setSuppressEmptyRecordsHidden(false);
    } else {
      setSuppressEmptyRecordsHidden(true);
    }

    //Field is Required
    if (["DATABASE", "XML"].includes(templateHeader.FILETYPE)) {
      // cbVis.required.hide = false;
      setRequiredHidden(false);
    } else {
      // cbVis.required.hide = true;
      setRequiredHidden(true);
    }

    //Suppress Trailing Field Delimiter - common
    if (["CSV", "EDI", "FIXED_LENGTH", "DELIMITED", "DELIM_FLD_ID", "HL7", "MULIT_DELIMITED", "SERVLET"].includes(templateHeader.FILETYPE)) {
      setSuppressTrailingFieldDelimiterHidden(false);
    } else {
      setSuppressTrailingFieldDelimiterHidden(true);
    }

    //Allow Rounding for Float - show if FIELDTYPE is FLOAT.
    if (
      ["CSV", "DATABASE", "EDI", "FIXED_LENGTH", "DELIMITED", "DELIM_FLD_ID", "MULIT_DELIMITED", "SERVLET", "JSON", "XML"].includes(templateHeader.FILETYPE) &&
      ["FLOAT"].includes(nodeField?.FIELDTYPE ? nodeField.FIELDTYPE : updatedSelectedNode.FIELDTYPE)
    ) {
      setAllowFloatRoundingHidden(false);
    } else {
      setAllowFloatRoundingHidden(true);
    }

    //Repeatable Field - Show for HL7 if alpha/byte
    let pNode = getParentNode(selectedNode, translatedData);
    if (
      ["HL7", "DEL"].includes(templateHeader.FILETYPE) &&
      ["ALPHA", "BIT_FLAG"].includes(updatedSelectedNode.FIELDTYPE)
    ) {
      setRepeatableFieldHidden(false);
    } else if (["DELIM_FLD_ID", "MULIT_DELIMITED", "EDI", "HL7"].includes(templateHeader.FILETYPE)) {
      if (templateHeader.FILETYPE === "EDI" && templateHeader.FILETYPE_VERSION === "4010") {
        setRepeatableFieldHidden(true);
      }else if (templateHeader.FILETYPE === "DELIM_FLD_ID" && rightTree.currentTab === "header") {
        setRepeatableFieldHidden(true);
      }else {
        setRepeatableFieldHidden(false);
      }
    } else {
      if (templateHeader.FILETYPE === "JSON") {
        setRepeatableFieldHidden(false);
      } else {
        setRepeatableFieldHidden(true);
      }
    }
    if (pNode?.FIELD_GROUP === "TRUE") setRepeatableFieldHidden(true);

    //DATABASE and JSON FILETYPE
    if (templateHeader.FILETYPE === "DATABASE" || templateHeader.FILETYPE === "JSON") {
      setConvertNullHidden(false);
      setConvertEmptyHidden(false);
      setIncludeNullHidden(false);

    } else {
      setConvertNullHidden(true);
      setConvertEmptyHidden(true);  
      setIncludeNullHidden(true);
    }

    //JSON FILETYPE - "Array Value" checkbox, same prop as Repeatable Field
    if (templateHeader.FILETYPE === "JSON") {
      setEscapeDataHidden(false);
    } else {
      setEscapeDataHidden(true);
    }

    //Suppress Empty Repetition Seperators - Show if HL7. Disabled if Repeatable Field is false.
    if (
      ["HL7"].includes(templateHeader.FILETYPE) &&
      ["ALPHA", "BIT_FLAG"].includes(updatedSelectedNode.FIELDTYPE) && repeatableField === true
    ) {
      if (repeatableField === true) {
        setSuppressEmptyRepSepDisabled(false);
      } else {
        setSuppressEmptyRepSepDisabled(true);
      }
    } else {
      setSuppressEmptyRepSepDisabled(true);
    }
  }

  const setInitialCheckboxVals = () => {
    //set all checkbox values based on updatedSelectedNode properties.
    // if (!updatedSelectedNode) return;
    // let sn = updatedSelectedNode;
    // const getVal = (propName) => {
    //   if (sn?.[propName] === "TRUE") return true;
    //   else if (sn?.[propName] === "FALSE") return false;
    //   else return false;
    // }

    //moved default values logic to state declaration. keeping this here to avoid breaking things. will move later.
    setCheckboxesLoaded(true);
  }

  useEffect(() => {
    setCheckboxVisibility();
    setInitialCheckboxVals();
  }, [updatedSelectedNode?.key, templateHeader.FILETYPE, templateHeader.FILETYPE_VERSION, nodeField?.FIELDTYPE, nodeField?.SUPPRESS_EMPTY_REP_SEPS, updatedSelectedNode?.FIELDTYPE]);

  const handlecDataChange = (val) => {
    setcData(val);
    modifyUpdatedNode("IS_CDATA", val ? "TRUE" : null);
  }

  const handleTrimDataChange = (val) => {
    setTrimData(val);
    modifyUpdatedNode("TRIMDATA", val ? "TRUE" : null);
  }

  const handleEscapeXMLDataChange = (val) => {
    setEscapeXMLData(val);
    modifyUpdatedNode("ESCAPE_XML_DATA", val ? "TRUE" : null);
  }

  const handleEscXMLDataInversedChange = (val) => {
    setEscXMLDataInversed(val);
    modifyUpdatedNode("DONT_ESCAPE_XML", !val ? "TRUE" : null);
  }
  
  const handleQuoteDataChange = (val) => {
    setQuoteData(val);
    modifyUpdatedNode("QUOTE_DATA", val ? "TRUE" : null);
  }

  const handleURLEncodeDataChange = (val) => {
    setURLEncodeData(val);
    modifyUpdatedNode("URLENCODE", val ? "TRUE" : null);
  }

  const handleSuppressTrailingFieldDelimiterChange = (val) => {
    setSuppressTrailingFieldDelimiter(val);
    modifyUpdatedNode("SUPPRESS_DELIMITER", val ? "TRUE" : null);
  }

  const handleIgnoreGlobalSubstitutionsChange = (val) => {
    setIgnoreGlobalSubstitutions(val);
    modifyUpdatedNode("IGNORE_GLOBAL_SUB", val ? "TRUE" : null);
  }

  const handleSuppressEmptyRecordsChange = (val) => {
    setSuppressEmptyRecords(val);
    modifyUpdatedNode("SUPPRESS_EMPTY_RECORDS", val ? "TRUE" : null);
  }

  const handleAllowFloatRoundingChange = (val) => {
    setAllowFloatRounding(val);
    modifyUpdatedNode("ROUND", val ? "TRUE" : null);
  }

  const handleRepeatableFieldChange = (val) => {
    setRepeatableField(val);
    modifyUpdatedNode("REPEATING", val ? "TRUE" : null);
  }

  const handleSuppressEmptyRepSepChange = (val) => {
    setSuppressEmptyRepSep(val);
    modifyUpdatedNode("SUPPRESS_EMPTY_REP_SEPS", val ? "TRUE" : null);
  }

  const handleSuppressAttrChange = (val) => {
    setSuppressAttr(val);
    modifyUpdatedNode("SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", val ? "TRUE" : null);
  }

  const handleConvertEmptyChange = (val) => {
    setConvertEmpty(val);
    modifyUpdatedNode("CONVERT_EMPTY", val ? "TRUE" : null);
  }
  const handleConvertNullChange = (val) => {
    setConvertNull(val);
    modifyUpdatedNode("CONVERT_NULL", val ? "TRUE" : null);
  }

  const handleIncludeNullChange = (val) => {
    setIncludeNull(val);
    modifyUpdatedNode("INCLUDE_NULL", val ? "TRUE" : null);
  }

  const handleEscapeDataChange = (val) => {
    setEscapeData(val);
    modifyUpdatedNode("ESCAPE_DATA", val ? "TRUE" : null);
  }

  const handleRequiredChange = (val) => {
    setRequired(val);
    modifyUpdatedNode("REQUIRED", val ? "TRUE" : null);
  }

  //useEffects to check template header for changes that could affect these field property checkbox states
  useEffect(() => {
    if (checkboxesLoaded && typeof templateHeader.INCLUDE_NULL_ALL === 'boolean') setIncludeNull(templateHeader.INCLUDE_NULL_ALL);
  }, [templateHeader.INCLUDE_NULL_ALL]);
  
  useEffect(() => {
    if (checkboxesLoaded && typeof templateHeader.INCLUDE_NULL === 'boolean') setIncludeNull(templateHeader.INCLUDE_NULL);
  }, [templateHeader.INCLUDE_NULL]);

  useEffect(() => {
    if (checkboxesLoaded && typeof templateHeader.QUOTE_DATA === 'boolean') setQuoteData(templateHeader.QUOTE_DATA);
  }, [templateHeader.QUOTE_DATA]);

  useEffect(() => {
    if (checkboxesLoaded && typeof templateHeader.TRIMDATA === 'boolean') setTrimData(templateHeader.TRIMDATA);
  }, [templateHeader.TRIMDATA]);

  useEffect(() => {
    if (checkboxesLoaded && typeof templateHeader.URLENCODE === 'boolean') setURLEncodeData(templateHeader.URLENCODE);
  }, [templateHeader.URLENCODE]);

  useEffect(() => {
    if (checkboxesLoaded && typeof templateHeader.ESCAPE_DATA === "boolean") setEscapeData(templateHeader.ESCAPE_DATA);
  }, [templateHeader.ESCAPE_DATA]);

  useEffect(() => {
    if (checkboxesLoaded) flagAsLoaded(true);
  }, [checkboxesLoaded]);

  return (
    <>
      <div className="flex flex-wrap">
        { // IS_CDATA - "CDATA Element" - XML Element
          (!cDataHidden) && (
            <DefaultCheckbox
              label={t("BTN_CDATA_ELEM")}
              name={"BTN_CDATA_ELEM"}
              value={cData}
              setValue={handlecDataChange}
            />
          )
        }
        { // ESCAPE_XML_DATA - "Escape XML Data"
          (!escapeXMLDataHidden && !isElement) && (
            <DefaultCheckbox
              label={t("BTN_ESCAPE_XML")}
              name={"BTN_ESCAPE_XML"}
              value={escapeXMLData}
              setValue={handleEscapeXMLDataChange}
            />
          )
        } 
        
        { // DONT_ESCAPE_XML - "Escape XML Data" - Elements
          (!escXMLDataInversedHidden && isElement) && (
            <DefaultCheckbox
              label={t("BTN_ESCAPE_XML")}
              name={"BTN_ESCAPE_XML"}
              value={escXMLDataInversed}
              setValue={handleEscXMLDataInversedChange}
            />
          )
        } 
        { // TRIMDATA - "Trim Data"
          (!trimDataHidden) && (
            <DefaultCheckbox
              label={t("BTN_TRIM_DATA")}
              name={"BTN_TRIM_DATA"}
              value={trimData}
              setValue={handleTrimDataChange}
            />
          )
        }
        { // QUOTE_DATA - "Quote Data"
          (!quoteDataHidden) && (
            <DefaultCheckbox
              label={t("BTN_QUOTE_DATA")}
              name={"BTN_QUOTE_DATA"}
              value={quoteData}
              setValue={handleQuoteDataChange}
            />
          )
        }
        { // URLENCODE - "URLEncode Data"
          (!URLEncodeDataHidden) && (
            <DefaultCheckbox
              label={t("BTN_URLENCODE_DATA")}
              name={"BTN_URLENCODE_DATA"}
              value={URLEncodeData}
              setValue={handleURLEncodeDataChange}
            />
          )
        }
        { // SUPPRESS_DELIMITER - "Suppress Trailing Field Delimiter"
          (!suppressTrailingFieldDelimiterHidden) && (
            <DefaultCheckbox
              label={t("BTN_SUPPRESS_TRAILING_FLD_DELIM")}
              name={"BTN_SUPPRESS_TRAILING_FLD_DELIM"}
              value={suppressTrailingFieldDelimiter}
              setValue={handleSuppressTrailingFieldDelimiterChange}
            />
          )
        }
        { // IGNORE_GLOBAL_SUB - "Ignore Global Substitutions"
          (!ignoreGlobalSubstitutionsHidden) && (
            <DefaultCheckbox
              label={t("IGNORE_GLOBAL_SUB")}
              name={"IGNORE_GLOBAL_SUB"}
              value={ignoreGlobalSubstitutions}
              setValue={handleIgnoreGlobalSubstitutionsChange}
            />
          )
        }
        { // ROUND - "Allow Rounding for Float Values"
          (!allowFloatRoundingHidden) && (
            <DefaultCheckbox
              label={t("BTN_ALLOW_ROUNDING_FOR_FLOAT")}
              name={"BTN_ALLOW_ROUNDING_FOR_FLOAT"}
              value={allowFloatRounding}
              setValue={handleAllowFloatRoundingChange}
            />
          )
        }
        { // REPEATING - "Repeatable Field" / "Array Value" if JSON filetype
          (!repeatableFieldHidden) && (
            <DefaultCheckbox
              label={templateHeader?.FILETYPE === "JSON" ? t("BTN_JSON_ARRAY") : t("BTN_FLD_REPEATABLE")}
              name={"BTN_FLD_REPEATABLE"}
              value={repeatableField}
              setValue={handleRepeatableFieldChange}
            />
          )
        }
        { // SUPPRESS_EMPTY_REP_SEPS - "Suppress Empty Repetition Separators"
          (!suppressEmptyRepSepHidden) && (
            <DefaultCheckbox
              label={t("BTN_SUPPRESS_EMPTY_REP_SEPS")}
              name={"BTN_SUPPRESS_EMPTY_REP_SEPS"}
              disabled={suppressEmptyRepSepDisabled}
              value={suppressEmptyRepSep}
              setValue={handleSuppressEmptyRepSepChange}
            />
          )
        }

        { // SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER - "Suppress Attributes when Data Empty"
          (!suppressAttrHidden) && (
            <DefaultCheckbox
              label={t("BTN_SUPP_ATTRIB_MSSNG_FLD_IDENT")}
              name={"BTN_SUPP_ATTRIB_MSSNG_FLD_IDENT"}
              value={suppressAttr}
              setValue={handleSuppressAttrChange}
            />
          )
        }
        
        { // REQUIRED - "Field is Required"
          (!requiredHidden) && (
            <DefaultCheckbox
              label={t("BTN_FLD_REQ")}
              name={"BTN_FLD_REQ"}
              value={required}
              setValue={handleRequiredChange}
            />
          )
        }
        { // SUPPRESS_EMPTY_RECORDS - "Suppress Empty Elements" - XML Element
          (!suppressEmptyRecordsHidden) && (
            <DefaultCheckbox
              label={t("BTN_SUPPRESS_EMPTY_RECORDS")}
              name={"BTN_SUPPRESS_EMPTY_RECORDS"}
              value={suppressEmptyRecords}
              setValue={handleSuppressEmptyRecordsChange}
            />
          )
        }
        { // CONVERT_EMPTY - "Convert Empty String to Null"
          (!convertEmptyHidden) && (
            <DefaultCheckbox
              label={t("BTN_CONVERT_TO_NULL")}
              name={"BTN_CONVERT_TO_NULL"}
              value={convertEmpty}
              setValue={handleConvertEmptyChange}
            />
          )
        }
        { // CONVERT_NULL - "Convert Null Values to Empty String"
          (!convertNullHidden) && (
            <DefaultCheckbox
              label={t("BTN_CONVERT_TO_EMPTY")}
              name={"BTN_CONVERT_TO_EMPTY"}
              value={convertNull}
              setValue={handleConvertNullChange}
            />
          )
        }
        { // INCLUDE_NULL - "Include if Null"
          (!includeNullHidden) && (
            <DefaultCheckbox
              label={templateHeader?.FILETYPE === "DATABASE" ? t("BTN_INCLUDE_COL_IF_NULL") : t("BTN_INCLUDE_IF_NULL")}
              name={"BTN_INCLUDE_IF_NULL"}
              value={includeNull}
              setValue={handleIncludeNullChange}
            />
          )
        }
        { // ESCAPE_DATA - "Escape Data"
          (!escapeDataHidden) && (
            <DefaultCheckbox
              label={t("BTN_ESCAPE_DATA")}
              name={"BTN_ESCAPE_DATA"}
              value={escapeData}
              setValue={handleEscapeDataChange}
            />
          )
        }
      </div >
    </>
  )
}
